import React, { useEffect, useState, Suspense } from 'react';
import { Box, Stack, ThemeProvider, Container } from '@mui/material';
import { Navigate, useLocation } from 'react-router-dom';
import { generateTheme } from "./themes";
import { detectLang, getSlug, getLangParam, DEFAULT_LANG } from './utils/language';
import { detectMarket, getSlug as getMarketSlug, loadMarkets } from './utils/market';
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import parse from 'html-react-parser';


import './App.css';
// Logo is now handled by PHP nav (wp-theme/nav.php)

// Components
// Menu is now rendered by PHP (wp-theme/nav.php) for consistency across all pages
import MetaTagsHelper from './components/utils/MetaTagsHelper';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

// Eagerly load above-fold hero (LCP element) — must not be lazy
import TopHeroSlice from './components/TopHeroSlice';
// Lazy load below-fold components
const KeyFeaturesSlice = React.lazy(() => import('./components/KeyFeaturesSlice'));
const TextImageSlice = React.lazy(() => import('./components/TextImageSlice'));
const TextTwoCols = React.lazy(() => import('./components/TextTwoCols'));
const SellingPointsSlice = React.lazy(() => import('./components/SellingPointsSlice'));
const PriceSlice = React.lazy(() => import('./components/PriceSlice'));
const ContactSlice = React.lazy(() => import('./components/ContactSlice'));
const CustomerLogos = React.lazy(() => import('./components/CustomerLogos'));
const CustomerStoriesSlice = React.lazy(() => import('./components/CustomerStoriesSlice'));
const Faq = React.lazy(() => import('./components/Faq'));
const FaqShortSlice = React.lazy(() => import('./components/FaqShortSlice'));
const ImageFullwidth = React.lazy(() => import('./components/ImageFullwidth'));
const PricingPlanSlice = React.lazy(() => import('./components/PricingPlanSlice'));
const CallToActionSlice = React.lazy(() => import('./components/CallToActionSlice'));
const InfoGridSlice = React.lazy(() => import('./components/InfoGridSlice'));
const GlobalReachSlice = React.lazy(() => import('./components/GlobalReachSlice'));
const KpiGridSlice = React.lazy(() => import('./components/KpiGridSlice'));
const FinalCtaSlice = React.lazy(() => import('./components/FinalCtaSlice'));
const SectionIntroSlice = React.lazy(() => import('./components/SectionIntroSlice'));
const FeaturesGridSlice = React.lazy(() => import('./components/FeaturesGridSlice'));
const ComparisonSlice = React.lazy(() => import('./components/ComparisonSlice'));
const TopSubpageSlice = React.lazy(() => import('./components/TopSubpageSlice'));
const ThankYouSlice = React.lazy(() => import('./components/ThankYouSlice'));

// Default content renderers
const DefaultPageRenderer = React.lazy(() => import('./components/DefaultPageRenderer'));
const DefaultPostRenderer = React.lazy(() => import('./components/DefaultPostRenderer'));


export const getLink = (linkData) => {

    if (!linkData) return null

    if (linkData.includes('#')) {
        return "#" + linkData.split("#")[1]
    } else {
        return linkData
    }
}


function App({ wpData }) {
    const [pageData, setPageData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const location = useLocation();

    // Log version on every render
    console.log('🚀 CardCam App v1.0.1 - Build:', new Date().toISOString());
    console.log('📍 Current path:', location.pathname);

    useEffect(() => {
        // Make sure the markets table from WP is loaded before resolving page
        // data. Resolves to the hardcoded fallback if the option / endpoint is
        // missing, so this never blocks the UI in dev.
        loadMarkets();
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const market = detectMarket(location.pathname);
                const slug = getMarketSlug(location.pathname);
                const langParam = `&lang=${market.lang}`;

                // Two-step page resolution:
                //   1. Try market-specific override slug (`${prefix}-${slug}`)
                //   2. Fall back to the canonical slug
                let contentData;
                if (market.prefix) {
                    const overrideSlug = `${market.prefix}-${slug}`;
                    const overrideRes = await fetch(`/wp-json/wp/v2/pages?slug=${overrideSlug}${langParam}`);
                    const overrideData = await overrideRes.json();
                    if (overrideData[0]) contentData = overrideData[0];
                }
                if (!contentData) {
                    const pageRes = await fetch(`/wp-json/wp/v2/pages?slug=${slug}${langParam}`);
                    const pagesData = await pageRes.json();
                    contentData = pagesData[0];
                }

                // If no page found, try posts endpoint (no market override —
                // posts are typically blog content shared across markets).
                if (!contentData && slug !== 'home') {
                    console.log('No page found, trying posts endpoint for slug:', slug);
                    const postRes = await fetch(`/wp-json/wp/v2/posts?slug=${slug}&_embed${langParam}`);
                    const postsData = await postRes.json();
                    contentData = postsData[0];

                    // Add featured image data if available
                    if (contentData && contentData._embedded && contentData._embedded['wp:featuredmedia']) {
                        const featuredMedia = contentData._embedded['wp:featuredmedia'][0];
                        contentData.featured_media_url = featuredMedia.source_url;
                        contentData.featured_media_alt = featuredMedia.alt_text;
                    }

                    console.log('Post data found:', contentData);
                }

                setPageData(contentData);

            } catch (error) {
                console.error('Error fetching data:', error);
            }
            setIsLoading(false);
        };

        fetchData();
    }, [location]);

    // Extract page metadata from Yoast SEO REST API field (yoast_head_json)
    const getPageMetadata = () => {
        if (!pageData) return null;

        const yoast = pageData.yoast_head_json || {};

        return {
            data: {
                meta_title: yoast.title || pageData.title?.rendered || '',
                meta_description: yoast.description || pageData.excerpt?.rendered?.replace(/<[^>]*>/g, '') || '',
                og_image: yoast.og_image?.[0]?.url || pageData.featured_media_url || null
            }
        };
    };

    // Handle URL parameters
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const errorParam = urlParams.get('error');
        if (errorParam) {
            setMessageId(errorParam);
        }
    }, []);

    // Keep <html lang> in sync with the active route so Cookiebot and other
    // language-aware widgets pick up the correct locale. Values match what
    // WordPress/Polylang emits server-side (see header.php language_attributes()).
    useEffect(() => {
        const lang = detectLang(location.pathname);
        document.documentElement.lang = lang === DEFAULT_LANG ? 'sv-SE' : 'en-GB';
    }, [location.pathname]);

    const generateContent = () => {
        if (!pageData?.content?.rendered) return null;

        const componentMap = {
            'wp-block-cardcam-hero': TopHeroSlice,
            'wp-block-cardcam-key-features': KeyFeaturesSlice,
            'wp-block-cardcam-text-image': TextImageSlice,
            'wp-block-cardcam-video-text': TextImageSlice,
            'wp-block-cardcam-text-two-cols': TextTwoCols,
            'wp-block-cardcam-selling-points': SellingPointsSlice,
            'wp-block-cardcam-pricing-plan': PricingPlanSlice,
            'wp-block-cardcam-pricing': PriceSlice,
            'wp-block-cardcam-contact': ContactSlice,
            'wp-block-cardcam-customer-logos': CustomerLogos,
            'wp-block-cardcam-customer-stories': CustomerStoriesSlice,
            'wp-block-cardcam-faq-short': FaqShortSlice,
            'wp-block-cardcam-faq': Faq,
            'wp-block-cardcam-faq-section': Faq,
            'wp-block-cardcam-image-full': ImageFullwidth,
            // wp-block-cardcam-footer is intentionally excluded — footer is now PHP-rendered (footer.php)
            'wp-block-cardcam-call-to-action': CallToActionSlice,
            'wp-block-cardcam-info-grid': InfoGridSlice,
            'wp-block-cardcam-global-reach': GlobalReachSlice,
            'wp-block-cardcam-kpi-grid': KpiGridSlice,
            'wp-block-cardcam-final-cta': FinalCtaSlice,
            'wp-block-cardcam-section-intro': SectionIntroSlice,
            'wp-block-cardcam-features-grid': FeaturesGridSlice,
            'wp-block-cardcam-comparison': ComparisonSlice,
            'wp-block-cardcam-thank-you': ThankYouSlice
        };

        // Create a DOM parser
        const parser = new DOMParser();
        const doc = parser.parseFromString(pageData.content.rendered, 'text/html');

        // Find all our CUSTOM CardCam blocks (excluding menu which is handled separately)
        const blocks = Array.from(doc.body.children).filter(node => {
            if (!node.className) return false;
            if (node.className.includes('wp-block-cardcam-menu')) return false;
            return Object.keys(componentMap).some(type => {
                const regex = new RegExp('(^|\\s)' + type.replace(/-/g, '\\-') + '(\\s|$)');
                return regex.test(node.className);
            });
        });

        console.log('Found custom CardCam blocks:', blocks.length);
        console.log('All page children:', Array.from(doc.body.children).map(n => n.className || n.tagName));

        // Helper function to extract data based on block type
        const extractBlockData = (block, blockType) => {
            console.log("%c-------------------------------", "color: cyan; font-weight: bold;");
            console.log("Extracting data for block type:", blockType);
            console.log(block);
            switch (blockType) {
                case 'wp-block-cardcam-hero':
                    return {
                        title: block.querySelector('h1')?.innerHTML || '',
                        description: block.querySelector('p')?.innerHTML || '',
                        imageUrl: block.querySelector('.hero-img')?.src || '',
                        videoUrl: block.querySelector('.hero-video source')?.src || '',
                        imagePosition: block.querySelector('.hero-image')?.classList.contains('right') ? 'right' : 'left',
                        colorTheme: block.className.match(/theme-([a-z0-9-]+)/)?.[1] || 'white',
                        cta: {
                            text: block.querySelector('.hero-cta')?.innerHTML || '',
                            url: block.querySelector('.hero-cta')?.href || ''
                        },
                        buttonStyle: block.querySelector('.hero-cta')?.dataset.buttonStyle || 'filled',
                        // New Prismic-compatible properties
                        tagline: block.querySelector('.hero-tagline')?.innerHTML || null,
                        tagline_image: block.querySelector('.tagline-section .tagline-image')?.src ? {
                            url: block.querySelector('.tagline-section .tagline-image').src,
                            alt: block.querySelector('.tagline-section .tagline-image').alt || ''
                        } : null,
                        sub_title_2: block.querySelector('.hero-subtitle2')?.innerHTML ? [{ text: block.querySelector('.hero-subtitle2').innerHTML }] : null,
                        button_sub_title: block.querySelector('.button-subtitle')?.innerHTML ? [{ text: block.querySelector('.button-subtitle').innerHTML }] : null,
                        secondary_cta_text: block.querySelector('.hero-secondary-cta')?.textContent || null,
                        secondary_cta_url: block.querySelector('.hero-secondary-cta')?.getAttribute('href') || null,
                        topBanner: block.dataset.topBannerVisible === 'true' ? {
                            text: block.dataset.topBannerText || '',
                            text2: block.dataset.topBannerText2 || '',
                            campaignText: block.dataset.topBannerCampaignText || '',
                            campaignText2: block.dataset.topBannerCampaignText2 || '',
                            bgColor: block.dataset.topBannerBg || '#5C33D7',
                            textColor: block.dataset.topBannerColor || '#ffffff',
                            link: block.dataset.topBannerLink || '',
                            size: block.dataset.topBannerSize || 'Small'
                        } : null
                    };
                case 'wp-block-cardcam-customer-logos':
                    const logoGrid = block.querySelector('.logos-grid');
                    const logos = Array.from(logoGrid?.querySelectorAll('.customer-logo-item') || []).map(item => ({
                        imageUrl: item.querySelector('img')?.src || '',
                        imageAlt: item.querySelector('img')?.alt || '',
                        url: item.querySelector('a')?.href || ''
                    }));


                    return {
                        eyebrow: block.querySelector('h2')?.innerHTML || '',
                        title: block.querySelector('h3')?.innerHTML || '',
                        ctaText: block.querySelector('.cta-text')?.innerHTML || '',
                        ctaLink: block.querySelector('.cta-link')?.getAttribute('href') || '',
                        colorTheme: block.className.match(/theme-([a-z0-9-]+)/)?.[1] || 'white',
                        logos: logos
                    };
                case 'wp-block-cardcam-global-reach': {
                    let countries = [];
                    try {
                        const parsed = JSON.parse(block.dataset.countries || '[]');
                        if (Array.isArray(parsed)) {
                            countries = parsed.filter(c => c && c.code && String(c.code).toLowerCase() !== 'unknown');
                        }
                    } catch (e) {
                        console.warn('Invalid countries JSON in global-reach block', e);
                    }
                    let kpis = [];
                    try {
                        const parsed = JSON.parse(block.dataset.kpis || '[]');
                        if (Array.isArray(parsed)) {
                            kpis = parsed.filter(k => k && k.value !== undefined && k.value !== '');
                        }
                    } catch (e) {
                        console.warn('Invalid kpis JSON in global-reach block', e);
                    }
                    const countEl = block.querySelector('.gr-count');
                    return {
                        colorTheme: block.className.match(/theme-([a-z0-9-]+)/)?.[1] || 'white',
                        eyebrow: block.querySelector('.gr-eyebrow')?.innerHTML || '',
                        kpisEyebrow: block.querySelector('.gr-kpis-eyebrow')?.innerHTML || '',
                        unitLabel: block.querySelector('.gr-unit')?.innerHTML || '',
                        tagline: block.querySelector('.gr-tagline')?.innerHTML || '',
                        numberOverride: countEl?.dataset.target || '',
                        maxVisibleFlags: parseInt(block.dataset.maxVisibleFlags, 10) || 9,
                        countries,
                        kpis
                    };
                }
                case 'wp-block-cardcam-kpi-grid': {
                    let kpis = [];
                    try {
                        const parsed = JSON.parse(block.dataset.kpis || '[]');
                        if (Array.isArray(parsed)) {
                            kpis = parsed.filter(k => k && k.value !== undefined && k.value !== '');
                        }
                    } catch (e) {
                        console.warn('Invalid kpis JSON in kpi-grid block', e);
                    }
                    return {
                        colorTheme: block.className.match(/theme-([a-z0-9-]+)/)?.[1] || 'white',
                        eyebrow: block.querySelector('.kg-eyebrow')?.innerHTML || '',
                        title: block.querySelector('.kg-title')?.innerHTML || '',
                        tagline: block.querySelector('.kg-tagline')?.innerHTML || '',
                        columns: parseInt(block.dataset.columns, 10) || 3,
                        kpis
                    };
                }
                case 'wp-block-cardcam-final-cta': {
                    const primaryEl = block.querySelector('.fc-primary-cta');
                    const secondaryEl = block.querySelector('.fc-secondary-cta');
                    return {
                        colorTheme: block.className.match(/theme-([a-z0-9-]+)/)?.[1] || 'dark',
                        eyebrow: block.querySelector('.fc-eyebrow')?.innerHTML || '',
                        title: block.querySelector('.fc-title')?.innerHTML || '',
                        subtitle: block.querySelector('.fc-subtitle')?.innerHTML || '',
                        titleSizeDesktop: block.dataset.titleSizeDesktop || 'l',
                        titleSizeMobile: block.dataset.titleSizeMobile || 'm',
                        primaryCta: primaryEl ? {
                            text: primaryEl.innerHTML || '',
                            url: block.dataset.primaryCtaUrl || primaryEl.getAttribute('href') || '',
                            style: block.dataset.primaryCtaStyle || 'filled'
                        } : null,
                        secondaryCta: secondaryEl ? {
                            text: secondaryEl.innerHTML || '',
                            url: block.dataset.secondaryCtaUrl || secondaryEl.getAttribute('href') || '',
                            style: block.dataset.secondaryCtaStyle || 'outlined-contrast'
                        } : null
                    };
                }
                case 'wp-block-cardcam-comparison': {
                    let comparison = { columns: [], rows: [] };
                    try {
                        const raw = block.dataset.comparison || '{}';
                        const parsed = JSON.parse(raw);
                        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                            comparison = parsed;
                        }
                    } catch (e) {
                        console.warn('Comparison: invalid comparison JSON', e);
                    }
                    return {
                        colorTheme: block.className.match(/theme-([a-z0-9-]+)/)?.[1] || 'off-white',
                        eyebrow: block.querySelector('.comparison-eyebrow')?.innerHTML || '',
                        title: block.querySelector('.comparison-title')?.innerHTML || '',
                        body: block.querySelector('.comparison-body')?.innerHTML || '',
                        footnote: block.querySelector('.comparison-footnote')?.innerHTML || '',
                        comparison,
                        gradientHeadline: block.dataset.gradientHeadline === 'true',
                        sliceMarginTop: block.dataset.sliceMarginTop || 'Auto',
                        sliceMarginTopMobile: block.dataset.sliceMarginTopMobile || 'Auto',
                        sliceMarginBottom: block.dataset.sliceMarginBottom || 'Auto',
                        sliceMarginBottomMobile: block.dataset.sliceMarginBottomMobile || 'Auto'
                    };
                }
                case 'wp-block-cardcam-thank-you': {
                    const safeJson = (raw) => {
                        try {
                            const v = JSON.parse(raw || '[]');
                            return Array.isArray(v) ? v : [];
                        } catch (e) { return []; }
                    };
                    return {
                        colorTheme: block.className.match(/theme-([a-z0-9-]+)/)?.[1] || 'off-white',
                        eyebrow: block.querySelector('.ty-eyebrow')?.innerHTML || '',
                        title: block.querySelector('.ty-title')?.innerHTML || '',
                        intro: block.querySelector('.ty-intro')?.innerHTML || '',
                        whatNextHeading: block.querySelector('.ty-what-next-heading')?.innerHTML || '',
                        steps: safeJson(block.dataset.steps),
                        directHeading: block.querySelector('.ty-direct-heading')?.innerHTML || '',
                        directEmail: block.dataset.directEmail || '',
                        backHomeLabel: block.dataset.backHomeLabel || '',
                        backHomeUrl: block.dataset.backHomeUrl || ''
                    };
                }
                case 'wp-block-cardcam-features-grid': {
                    let categories = [];
                    try {
                        const raw = block.dataset.categories || '[]';
                        const parsed = JSON.parse(raw);
                        if (Array.isArray(parsed)) categories = parsed;
                    } catch (e) {
                        console.warn('FeaturesGrid: invalid categories JSON', e);
                    }
                    return {
                        colorTheme: block.className.match(/theme-([a-z0-9-]+)/)?.[1] || 'off-white',
                        columns: block.dataset.columns || '2',
                        eyebrow: block.querySelector('.features-grid-eyebrow')?.innerHTML || '',
                        title: block.querySelector('.features-grid-title')?.innerHTML || '',
                        body: block.querySelector('.features-grid-body')?.innerHTML || '',
                        categories,
                        gradientHeadline: block.dataset.gradientHeadline === 'true',
                        centerAlign: block.dataset.centerAlign === 'true',
                        titleSizeDesktop: block.dataset.titleSizeDesktop || 'l',
                        titleSizeMobile: block.dataset.titleSizeMobile || 'm',
                        sliceMarginTop: block.dataset.sliceMarginTop || 'Auto',
                        sliceMarginTopMobile: block.dataset.sliceMarginTopMobile || 'Auto',
                        sliceMarginBottom: block.dataset.sliceMarginBottom || 'Auto',
                        sliceMarginBottomMobile: block.dataset.sliceMarginBottomMobile || 'Auto'
                    };
                }
                case 'wp-block-cardcam-section-intro': {
                    const primaryEl = block.querySelector('.si-primary-cta');
                    const secondaryEl = block.querySelector('.si-secondary-cta');
                    return {
                        colorTheme: block.className.match(/theme-([a-z0-9-]+)/)?.[1] || 'off-white',
                        eyebrow: block.querySelector('.si-eyebrow')?.innerHTML || '',
                        title: block.querySelector('.si-title')?.innerHTML || '',
                        body: block.querySelector('.si-body')?.innerHTML || '',
                        titleSizeDesktop: block.dataset.titleSizeDesktop || 'l',
                        titleSizeMobile: block.dataset.titleSizeMobile || 'm',
                        align: block.dataset.align || 'left',
                        gradientHeadline: block.dataset.gradientHeadline === 'true',
                        pinWhileScrolling: block.dataset.pinWhileScrolling !== 'false',
                        primaryCta: primaryEl ? {
                            text: primaryEl.innerHTML || '',
                            url: block.dataset.primaryCtaUrl || primaryEl.getAttribute('href') || '',
                            style: block.dataset.primaryCtaStyle || 'filled'
                        } : null,
                        secondaryCta: secondaryEl ? {
                            text: secondaryEl.innerHTML || '',
                            url: block.dataset.secondaryCtaUrl || secondaryEl.getAttribute('href') || '',
                            style: block.dataset.secondaryCtaStyle || 'outlined'
                        } : null
                    };
                }
                case 'wp-block-cardcam-text-image':
                    // Extract all the content from the WordPress block
                    const contentWrapper = block.querySelector('.content-wrapper');
                    const textContent = contentWrapper?.querySelector('.text-content');
                    const imageContent = contentWrapper?.querySelector('.image-content');

                    const subtitleHtml = textContent?.querySelector('h3')?.innerHTML || '';
                    const descriptionHtml = textContent?.querySelector('.slice-description')?.innerHTML || '';

                    const extractedData = {
                        colorTheme: block.className.match(/theme-([a-z0-9-]+)/)?.[1] || 'white',
                        eyebrow: textContent?.querySelector('.text-image-eyebrow')?.innerHTML || '',
                        eyebrowStyle: block.dataset.eyebrowStyle || 'standard',
                        pills: Array.from(textContent?.querySelectorAll('.text-image-pill') || []).map(p => p.textContent).filter(Boolean),
                        gradientHeadline: block.dataset.gradientHeadline === 'true',
                        listStyle: block.dataset.listStyle || 'arrow',
                        title: textContent?.querySelector('h2')?.innerHTML || '',
                        subtitle: subtitleHtml,
                        description: descriptionHtml,
                        buttonText: textContent?.querySelector('a')?.textContent || '',
                        buttonLink: textContent?.querySelector('a')?.getAttribute('href') || '',
                        buttonType: block.dataset.buttonType || 'filled_primary',
                        imageUrl: imageContent?.querySelector('img')?.src || '',
                        videoUrl: block.dataset.videoUrl || '',
                        videoFallback: block.dataset.videoFallback || '',
                        anchor: block.dataset.anchor || '',
                        shadowedBorders: block.dataset.shadowedBorders || 'No',
                        margins: block.dataset.margins || 'Normal',
                        imageSizeMobile: block.dataset.imageSizeMobile || 'Normal',
                        sliderImageSize: block.dataset.sliderImageSize || 'Normal',
                        secondaryImageUrl: block.dataset.secondaryImage || block.querySelector('.secondary-image')?.src || '',
                        parallax: block.dataset.parallax === 'true',
                        imageMarginTop: block.dataset.imageMarginTop || 'None',
                        imageMarginBottom: block.dataset.imageMarginBottom || 'None',
                        imageMarginTopMobile: block.dataset.imageMarginTopMobile || 'Normal',
                        imageMarginBottomMobile: block.dataset.imageMarginBottomMobile || 'Normal',
                        imageAlignment: block.dataset.imageAlignment || 'Auto',
                        imageMarginLeft: block.dataset.imageMarginLeft || 'None',
                        imageMarginRight: block.dataset.imageMarginRight || 'None',
                        imageMarginLeftMobile: block.dataset.imageMarginLeftMobile || 'None',
                        imageMarginRightMobile: block.dataset.imageMarginRightMobile || 'None',
                        headlineSize: block.dataset.headlineSize || 'Big',
                        sliceMarginTop: block.dataset.sliceMarginTop || 'Auto',
                        sliceMarginTopMobile: block.dataset.sliceMarginTopMobile || 'Auto',
                        sliceMarginBottom: block.dataset.sliceMarginBottom || 'Auto',
                        sliceMarginBottomMobile: block.dataset.sliceMarginBottomMobile || 'Auto',
                        textImageAlignment: block.className.includes('text-small-left') ? 'Text small left - Image right' :
                                          block.className.includes('text-small-right') ? 'Image left - Text small right' :
                                          block.className.includes('image-small-left') ? 'Image small left - Text right' :
                                          block.className.includes('image-left') ? 'Image left - Text right' :
                                          'Text left - Image right'
                    };
                    
                    
                    return extractedData;
                case 'wp-block-cardcam-key-features':
                    return {
                        domNode: block,
                        content: block.children
                    };
                case 'wp-block-cardcam-video-text':
                    return {
                        colorTheme: block.dataset.colorTheme || 'white',
                        eyebrow: block.querySelector('.section-eyebrow')?.innerHTML || '',
                        gradientHeadline: block.dataset.gradientHeadline === 'true',
                        title: block.querySelector('.section-title')?.innerHTML || '',
                        subtitle: block.querySelector('.section-subtitle')?.innerHTML || '',
                        videoUrl: block.dataset.videoUrl || '',
                        videoFallback: block.dataset.videoFallback || '',
                        buttonText: block.querySelector('.section-button')?.textContent || '',
                        buttonLink: block.querySelector('.section-button')?.href || '',
                        buttonType: block.dataset.buttonType || 'filled_primary',
                        textImageAlignment: 'Text left - Image right',
                        margins: 'Normal',
                        imageSizeMobile: 'Normal',
                        sliderImageSize: 'Normal',
                        shadowedBorders: 'No',
                        parallax: false
                    };
                case 'wp-block-cardcam-footer':
                    return {
                        headlineStory: block.querySelector('.headline-story')?.innerHTML || '',
                        headlineText: block.querySelector('.headline-text')?.innerHTML || '',
                        linksHeadline: block.querySelector('.links-headline')?.innerHTML || '',
                        linksLinks: block.querySelector('.links-links')?.innerHTML || '',
                        followHeadline: block.querySelector('.follow-headline')?.innerHTML || '',
                        followLinks: Array.from(block.querySelectorAll('.social-link-item')).map(item => {
                            const itemData = item.dataset.socialData;
                            if (itemData) {
                                try {
                                    return JSON.parse(itemData);
                                } catch (e) {
                                    console.warn('Failed to parse social link data:', e);
                                }
                            }
                            // Fallback to DOM extraction
                            return {
                                linkImage: item.querySelector('.social-link-image')?.src ? {
                                    url: item.querySelector('.social-link-image').src,
                                    alt: item.querySelector('.social-link-image').alt || ''
                                } : null,
                                linkUrl: item.querySelector('.social-link')?.href || '',
                                linkAlt: item.querySelector('.social-link-image')?.alt || ''
                            };
                        }),
                        addressHeadline: block.querySelector('.address-headline')?.innerHTML || '',
                        addressText: block.querySelector('.address-text')?.innerHTML || '',
                        copyrightText: block.querySelector('.copyright-text')?.innerHTML || '',
                        logo: {
                            url: block.querySelector('.footer-logo')?.src || '',
                            alt: block.querySelector('.footer-logo')?.alt || ''
                        },
                        colorTheme: block.className.match(/theme-([a-z0-9-]+)/)?.[1] || 'white'
                    };
                case 'wp-block-cardcam-info-grid':
                    const infoGridItems = Array.from(block.querySelectorAll('.info-grid-item')).map(item => {
                        const itemData = item.dataset.itemData;
                        if (itemData) {
                            try {
                                return JSON.parse(itemData);
                            } catch (e) {
                                console.warn('Failed to parse info grid item data:', e);
                            }
                        }
                        // Fallback to DOM extraction
                        return {
                            image: item.querySelector('.info-item-image')?.src ? {
                                url: item.querySelector('.info-item-image').src,
                                alt: item.querySelector('.info-item-image').alt || ''
                            } : null,
                            title: item.querySelector('.info-item-title')?.innerHTML || '',
                            text: item.querySelector('.info-item-text')?.innerHTML || '',
                            primaryLink: item.querySelector('.primary-link')?.href || '',
                            primaryLinkText: item.querySelector('.primary-link')?.dataset.linkText || '',
                            secondaryLink: item.querySelector('.secondary-link')?.href || '',
                            secondaryLinkText: item.querySelector('.secondary-link')?.dataset.linkText || '',
                            buttonStyle: item.querySelector('.primary-link')?.dataset.buttonStyle || 'filled'
                        };
                    });
                    return {
                        colorTheme: block.dataset.colorTheme || 'white',
                        items: infoGridItems
                    };
                case 'wp-block-cardcam-selling-points':
                    const sellingPointItems = Array.from(block.querySelectorAll('.selling-point-item')).map(item => {
                        const itemData = item.dataset.pointData;
                        if (itemData) {
                            try {
                                return JSON.parse(itemData);
                            } catch (e) {
                                console.warn('Failed to parse selling point item data:', e);
                            }
                        }
                        // Fallback to DOM extraction
                        return {
                            icon: item.querySelector('.selling-point-icon')?.src ? {
                                url: item.querySelector('.selling-point-icon').src,
                                alt: item.querySelector('.selling-point-icon').alt || ''
                            } : null,
                            eyebrow: item.querySelector('.selling-point-eyebrow')?.innerHTML || '',
                            title: item.querySelector('.selling-point-title')?.innerHTML || '',
                            description: item.querySelector('.selling-point-description')?.innerHTML || '',
                            itemStyle: item.dataset.itemStyle || 'default',
                            imagePosition: item.dataset.imagePosition || 'default',
                            layout: item.dataset.layout || 'default',
                            panelColor: item.dataset.panelColor || '',
                            panelGradient: item.dataset.panelGradient === 'true',
                            cardColor: item.dataset.cardColor || '',
                            cardGradient: item.dataset.cardGradient === 'true'
                        };
                    });
                    return {
                        colorTheme: block.dataset.colorTheme || 'white',
                        margins: block.dataset.margins || 'Normal',
                        columns: block.dataset.columns || 'auto',
                        cardStyle: block.dataset.cardStyle || 'plain',
                        iconSize: block.dataset.iconSize || 'medium',
                        eyebrow: block.querySelector('.selling-points-eyebrow')?.innerHTML || '',
                        sectionTitle: block.querySelector('.selling-points-title')?.innerHTML || '',
                        sectionSubtitle: block.querySelector('.selling-points-subtitle')?.innerHTML || '',
                        items: sellingPointItems,
                        sliceMarginTop: block.dataset.sliceMarginTop || 'Auto',
                        sliceMarginTopMobile: block.dataset.sliceMarginTopMobile || 'Auto',
                        sliceMarginBottom: block.dataset.sliceMarginBottom || 'Auto',
                        sliceMarginBottomMobile: block.dataset.sliceMarginBottomMobile || 'Auto'
                    };
                case 'wp-block-cardcam-faq':
                    const faqItems = Array.from(block.querySelectorAll('.faq-item')).map(item => {
                        const itemData = item.dataset.faqData;
                        if (itemData) {
                            try {
                                return JSON.parse(itemData);
                            } catch (e) {
                                console.warn('Failed to parse FAQ item data:', e);
                            }
                        }
                        // Fallback to DOM extraction matching Prismic structure
                        return {
                            questionHeadline: item.querySelector('.question-headline')?.innerHTML || '',
                            question: item.querySelector('h3')?.innerHTML || '',
                            answer: item.querySelector('.answer-text')?.innerHTML || ''
                        };
                    });
                    return {
                        colorTheme: block.className.match(/theme-([a-z0-9-]+)/)?.[1] || 'white',
                        eyebrowHeadline: block.querySelector('.eyebrow-headline')?.innerHTML || '',
                        title: block.querySelector('h2')?.innerHTML || '',
                        description: block.querySelector('.main-description')?.innerHTML || '',
                        postHeadline: block.querySelector('.post-headline')?.innerHTML || '',
                        postDescription: block.querySelector('.post-description')?.innerHTML || '',
                        buttonLink: block.querySelector('.cta-button')?.getAttribute('href') ? { url: block.querySelector('.cta-button').getAttribute('href') } : '',
                        buttonText: block.querySelector('.cta-button')?.textContent || '',
                        secondaryButtonLink: block.querySelector('.secondary-cta-button')?.getAttribute('href') ? { url: block.querySelector('.secondary-cta-button').getAttribute('href') } : '',
                        secondaryButtonText: block.querySelector('.secondary-cta-button')?.textContent || '',
                        optionalImage: block.querySelector('.optional-image')?.src ? {
                            url: block.querySelector('.optional-image').src,
                            alt: block.querySelector('.optional-image').alt || ''
                        } : null,
                        items: faqItems
                    };
                case 'wp-block-cardcam-faq-short':
                    const faqShortItems = Array.from(block.querySelectorAll('.faq-item')).map(item => {
                        const itemData = item.dataset.faqData;
                        if (itemData) {
                            try {
                                return JSON.parse(itemData);
                            } catch (e) {
                                console.warn('Failed to parse FAQ item data:', e);
                            }
                        }
                        // Fallback to DOM extraction
                        return {
                            questionHeadline: item.querySelector('.faq-category')?.innerHTML || '',
                            question: item.querySelector('.faq-question')?.innerHTML || '',
                            answer: item.querySelector('.faq-answer')?.innerHTML || ''
                        };
                    });
                    return {
                        colorTheme: block.dataset.colorTheme || 'white',
                        headline: block.querySelector('.faq-headline')?.innerHTML || '',
                        body: block.querySelector('.faq-body')?.innerHTML || '',
                        image: block.querySelector('.faq-image')?.src ? {
                            url: block.querySelector('.faq-image').src,
                            alt: block.querySelector('.faq-image').alt || ''
                        } : null,
                        postHeadline: block.querySelector('.faq-post-headline')?.innerHTML || '',
                        postDescription: block.querySelector('.faq-post-description')?.innerHTML || '',
                        buttonLink: block.querySelector('.faq-button')?.getAttribute('href') ? { url: block.querySelector('.faq-button').getAttribute('href') } : '',
                        buttonText: block.querySelector('.faq-button')?.textContent || '',
                        items: faqShortItems
                    };
                case 'wp-block-cardcam-text-two-cols':
                    const twocolData = block.dataset.blockData;
                    if (twocolData) {
                        try {
                            const parsed = JSON.parse(twocolData);
                            return {
                                primary: {
                                    color_theme: block.dataset.colorTheme || 'white',
                                    headline1: parsed.headline1 ? [{ type: 'heading1', text: parsed.headline1.replace(/<[^>]*>/g, ''), spans: [] }] : [],
                                    body1: parsed.body1 ? [{ type: 'paragraph', text: parsed.body1.replace(/<[^>]*>/g, ''), spans: [] }] : [],
                                    linktext1: parsed.linktext1 || '',
                                    link1: parsed.link1 ? { url: parsed.link1, link_type: 'Web', target: '_blank' } : { link_type: 'Any' },
                                    headline2: parsed.headline2 ? [{ type: 'heading1', text: parsed.headline2.replace(/<[^>]*>/g, ''), spans: [] }] : [],
                                    body2: parsed.body2 ? [{ type: 'paragraph', text: parsed.body2.replace(/<[^>]*>/g, ''), spans: [] }] : [],
                                    linktext2: parsed.linktext2 || '',
                                    link2: parsed.link2 ? { url: parsed.link2, link_type: 'Web', target: '_blank' } : { link_type: 'Any' }
                                }
                            };
                        } catch (e) {
                            console.warn('Failed to parse text-two-cols data:', e);
                        }
                    }
                    return {
                        primary: {
                            color_theme: block.dataset.colorTheme || 'white',
                            headline1: [{ type: 'heading1', text: block.querySelector('.col1-headline')?.textContent || '', spans: [] }],
                            body1: [{ type: 'paragraph', text: block.querySelector('.col1-body')?.textContent || '', spans: [] }],
                            linktext1: block.querySelector('.col1-link')?.dataset.linkText || block.querySelector('.col1-link')?.textContent || '',
                            link1: block.querySelector('.col1-link')?.href ? { url: block.querySelector('.col1-link').href, link_type: 'Web', target: '_blank' } : { link_type: 'Any' },
                            headline2: [{ type: 'heading1', text: block.querySelector('.col2-headline')?.textContent || '', spans: [] }],
                            body2: [{ type: 'paragraph', text: block.querySelector('.col2-body')?.textContent || '', spans: [] }],
                            linktext2: block.querySelector('.col2-link')?.dataset.linkText || block.querySelector('.col2-link')?.textContent || '',
                            link2: block.querySelector('.col2-link')?.href ? { url: block.querySelector('.col2-link').href, link_type: 'Web', target: '_blank' } : { link_type: 'Any' }
                        }
                    };
                case 'wp-block-cardcam-image-full':
                    const imgData = block.dataset.imageData;
                    if (imgData) {
                        try {
                            const parsed = JSON.parse(imgData);
                            return {
                                primary: {
                                    color_theme: block.dataset.colorTheme || 'white',
                                    image: parsed.image ? {
                                        url: parsed.image.url,
                                        alt: parsed.image.alt || '',
                                        dimensions: {
                                            width: parsed.image.width || 2000,
                                            height: parsed.image.height || 400
                                        }
                                    } : null,
                                    effect: parsed.effect || 'scroll'
                                }
                            };
                        } catch (e) {
                            console.warn('Failed to parse image-full data:', e);
                        }
                    }
                    const fullImg = block.querySelector('.fullwidth-image');
                    return {
                        primary: {
                            color_theme: block.dataset.colorTheme || 'white',
                            image: fullImg ? {
                                url: fullImg.src,
                                alt: fullImg.alt || '',
                                dimensions: {
                                    width: parseInt(fullImg.dataset.width) || 2000,
                                    height: parseInt(fullImg.dataset.height) || 400
                                }
                            } : null,
                            effect: fullImg?.dataset.effect || 'scroll'
                        }
                    };
                case 'wp-block-cardcam-call-to-action':
                    // Helper: extract selling points, handling escaped <li> tags
                    const extractSellingPoints = (parentBlock) => {
                        const ul = parentBlock.querySelector('.cta-selling-points');
                        let items = Array.from(parentBlock.querySelectorAll('.cta-selling-points li')).map(li => ({
                            type: 'list-item',
                            text: li.textContent || '',
                            spans: []
                        }));
                        if (items.length === 0 && ul) {
                            const raw = ul.textContent || '';
                            if (raw.includes('<li>')) {
                                const tmp = new DOMParser().parseFromString('<ul>' + raw + '</ul>', 'text/html');
                                items = Array.from(tmp.querySelectorAll('li')).map(li => ({
                                    type: 'list-item',
                                    text: li.textContent || '',
                                    spans: []
                                }));
                            }
                        }
                        return items;
                    };
                    const ctaBlockData = block.dataset.blockData;
                    if (ctaBlockData) {
                        try {
                            const parsed = JSON.parse(ctaBlockData);
                            return {
                                primary: {
                                    color_theme: block.dataset.colorTheme || 'white',
                                    headline: parsed.headline ? [{ type: 'heading1', text: parsed.headline.replace(/<[^>]*>/g, ''), spans: [] }] : [],
                                    bodytext: parsed.bodytext ? [{ type: 'paragraph', text: parsed.bodytext.replace(/<[^>]*>/g, ''), spans: [] }] : [],
                                    selling_points: extractSellingPoints(block),
                                    cta_text: parsed.ctaText || '',
                                    cta_link: parsed.ctaLink ? { url: parsed.ctaLink, link_type: 'Web', target: '_blank' } : { link_type: 'Any' },
                                    cta_subtitle: parsed.ctaSubtitle ? [{ type: 'paragraph', text: parsed.ctaSubtitle, spans: [] }] : [],
                                    button_style: parsed.buttonStyle || block.querySelector('.cta-button')?.dataset.buttonStyle || 'filled'
                                }
                            };
                        } catch (e) {
                            console.warn('Failed to parse call-to-action data:', e);
                        }
                    }
                    return {
                        primary: {
                            color_theme: block.dataset.colorTheme || 'white',
                            headline: [{ type: 'heading1', text: block.querySelector('.cta-headline')?.textContent || '', spans: [] }],
                            bodytext: [{ type: 'paragraph', text: block.querySelector('.cta-bodytext')?.textContent || '', spans: [] }],
                            selling_points: extractSellingPoints(block),
                            cta_text: block.querySelector('.cta-button')?.textContent || '',
                            cta_link: block.querySelector('.cta-button')?.getAttribute('href') ? { url: block.querySelector('.cta-button').getAttribute('href'), link_type: 'Web', target: '_blank' } : { link_type: 'Any' },
                            cta_subtitle: block.querySelector('.cta-subtitle')?.textContent ? [{ type: 'paragraph', text: block.querySelector('.cta-subtitle').textContent, spans: [] }] : [],
                            button_style: block.querySelector('.cta-button')?.dataset.buttonStyle || 'filled'
                        }
                    };
                case 'wp-block-cardcam-faq-section':
                    const faqSectionItems = Array.from(block.querySelectorAll('.faq-item')).map(item => {
                        const faqItemData = item.dataset.faqData;
                        if (faqItemData) {
                            try {
                                return JSON.parse(faqItemData);
                            } catch (e) {
                                console.warn('Failed to parse FAQ section item data:', e);
                            }
                        }
                        return {
                            questionHeadline: item.querySelector('.question-headline')?.innerHTML || '',
                            question: item.querySelector('h3')?.innerHTML || '',
                            answer: item.querySelector('.answer-text')?.innerHTML || ''
                        };
                    });
                    return {
                        colorTheme: block.className.match(/theme-([a-z0-9-]+)/)?.[1] || 'white',
                        eyebrowHeadline: block.querySelector('.eyebrow-headline')?.innerHTML || '',
                        title: block.querySelector('h2')?.innerHTML || '',
                        description: block.querySelector('.main-description')?.innerHTML || '',
                        postHeadline: block.querySelector('.post-headline')?.innerHTML || '',
                        postDescription: block.querySelector('.post-description')?.innerHTML || '',
                        buttonLink: block.querySelector('.cta-button')?.getAttribute('href') ? { url: block.querySelector('.cta-button').getAttribute('href') } : '',
                        buttonText: block.querySelector('.cta-button')?.textContent || '',
                        optionalImage: block.querySelector('.optional-image')?.src ? {
                            url: block.querySelector('.optional-image').src,
                            alt: block.querySelector('.optional-image').alt || ''
                        } : null,
                        items: faqSectionItems
                    };
                case 'wp-block-cardcam-pricing':
                    // Handle PriceSlice data extraction
                    return {
                        primary: {
                            color_theme: block.className.match(/theme-([a-z0-9-]+)/)?.[1] || 'white'
                        },
                        items: [] // Add logic here if there's pricing data to extract
                    };
                case 'wp-block-cardcam-menu':
                    // Extract menu data
                    const menuMessages = Array.from(block.querySelectorAll('.menu-message-item')).map(item => ({
                        messageId: item.dataset.messageId || '',
                        messageType: item.dataset.messageType || 'alert',
                        message: item.querySelector('.message-content')?.innerHTML || ''
                    }));
                    
                    // Extract menu links from the links HTML
                    const linksElement = block.querySelector('.menu-links');
                    let menuLinks = [];
                    if (linksElement) {
                        const linkElements = linksElement.querySelectorAll('a');
                        menuLinks = Array.from(linkElements).map(link => ({
                            title: link.textContent || link.innerHTML,
                            url: link.href || '#'
                        }));
                    }
                    
                    return {
                        colorTheme: block.dataset.colorTheme || 'white',
                        logo: block.querySelector('.menu-logo')?.src ? {
                            url: block.querySelector('.menu-logo').src,
                            alt: block.querySelector('.menu-logo').alt || ''
                        } : null,
                        links: linksElement?.innerHTML || '',
                        ctaLinkTitle: block.querySelector('.cta-link-title')?.innerHTML || '',
                        ctaLink: block.querySelector('.cta-link')?.href || '',
                        menuLinks: menuLinks, // Parsed links for easier consumption
                        messages: menuMessages
                    };
                case 'wp-block-cardcam-contact': {
                    const safeJson = (raw) => {
                        try {
                            const v = JSON.parse(raw || '[]');
                            return Array.isArray(v) ? v : [];
                        } catch (e) { return []; }
                    };
                    // UI strings (button label, snackbar messages) ship as block
                    // attribute defaults that the translation pipeline can't reach
                    // — when the dataset value matches a known SV default, swap to
                    // the language-appropriate copy instead of trusting it blindly.
                    const isEnglish = typeof window !== 'undefined' && window.location.pathname.startsWith('/en/');
                    const localizedDefault = (sv, en, dataValue) =>
                        (!dataValue || dataValue === sv) ? (isEnglish ? en : sv) : dataValue;
                    return {
                        colorTheme: block.className.match(/theme-([a-z0-9-]+)/)?.[1] || 'off-white',
                        eyebrow: block.querySelector('.contact-eyebrow')?.innerHTML || '',
                        title: block.querySelector('.contact-title')?.innerHTML || '',
                        description: block.querySelector('.contact-description')?.innerHTML || '',
                        benefits: safeJson(block.dataset.benefits),
                        infoTag: block.querySelector('.contact-info-tag')?.innerHTML || '',
                        directContactHeading: block.querySelector('.contact-direct-heading')?.innerHTML || '',
                        directContacts: safeJson(block.dataset.directContacts),
                        customersHeading: block.querySelector('.contact-customers-heading')?.innerHTML || '',
                        customers: safeJson(block.dataset.customers),
                        formHeading: block.querySelector('.contact-form-heading')?.innerHTML || '',
                        formSubheading: block.querySelector('.contact-form-subheading')?.innerHTML || '',
                        privacyNote: block.querySelector('.contact-privacy-note')?.innerHTML || '',
                        submitLabel:    localizedDefault('Skicka och få återkoppling',     'Send and get a reply',                   block.dataset.submitLabel),
                        successMessage: localizedDefault('Tack för ditt meddelande!',      'Thanks for your message!',               block.dataset.successMessage),
                        errorMessage:   localizedDefault('Någonting gick fel, försök igen!','Something went wrong, please try again!', block.dataset.errorMessage)
                    };
                }
                case 'wp-block-cardcam-customer-stories':
                    const customerStoryItems = Array.from(block.querySelectorAll('.customer-story-item')).map(item => {
                        const itemData = item.dataset.storyData;
                        if (itemData) {
                            try {
                                return JSON.parse(itemData);
                            } catch (e) {
                                console.warn('Failed to parse customer story item data:', e);
                            }
                        }
                        // Fallback to DOM extraction
                        return {
                            companyLogo: item.querySelector('.company-logo')?.src ? {
                                url: item.querySelector('.company-logo').src,
                                alt: item.querySelector('.company-logo').alt || ''
                            } : null,
                            customerMainImage: item.querySelector('.customer-main-image')?.src ? {
                                url: item.querySelector('.customer-main-image').src,
                                alt: item.querySelector('.customer-main-image').alt || ''
                            } : null,
                            customerPhoto: item.querySelector('.customer-photo')?.src ? {
                                url: item.querySelector('.customer-photo').src,
                                alt: item.querySelector('.customer-photo').alt || ''
                            } : null,
                            quoteHeadline: item.querySelector('.story-headline')?.innerHTML || '',
                            testimony: item.querySelector('.story-testimony')?.innerHTML || '',
                            customerName: item.querySelector('.customer-name')?.innerHTML || '',
                            customerPosition: item.querySelector('.customer-position')?.innerHTML || ''
                        };
                    });
                    return {
                        colorTheme: block.className.match(/theme-([a-z0-9-]+)/)?.[1] || 'white',
                        items: customerStoryItems
                    };
                case 'wp-block-cardcam-pricing-plan': {
                    const pricingPlanItems = Array.from(block.querySelectorAll('.pricing-plan-item')).map(item => {
                        const raw = item.dataset.planData;
                        let parsed = {};
                        if (raw) {
                            try { parsed = JSON.parse(raw); }
                            catch (e) { console.warn('Failed to parse pricing plan item data:', e); }
                        }
                        const features = parsed.features
                            ? parsed.features.replace(/<li>/g, '').split('</li>').filter(f => f.trim()).map(f => f.trim())
                            : Array.from(item.querySelectorAll('.plan-features li')).map(li => li.innerHTML || '');

                        let price = parsed.price || item.querySelector('.plan-price')?.innerHTML || '';
                        let priceCurrency = parsed.priceCurrency ?? item.querySelector('.plan-price-currency')?.innerHTML ?? item.dataset.priceCurrency ?? '';
                        let priceCurrencyPosition = parsed.priceCurrencyPosition || item.dataset.priceCurrencyPosition || 'suffix';

                        // If the price came in via the `{price_with_currency}` placeholder, PHP wrapped
                        // the symbol in <span class='cc-currency'>…</span>. Split it back out into the
                        // separate price/priceCurrency/position fields so PricingPlanSlice can style
                        // the symbol smaller using the existing amount-style branch. Only do this when
                        // the editor didn't fill in priceCurrency manually — manual entries win.
                        if (!priceCurrency && price && /class=['"]cc-currency['"]/i.test(price)) {
                            const prefixMatch = price.match(/^\s*<span\s+class=['"]cc-currency['"]\s*>([^<]*)<\/span>\s*([\s\S]*)$/i);
                            const suffixMatch = price.match(/^([\s\S]*?)\s*<span\s+class=['"]cc-currency['"]\s*>([^<]*)<\/span>\s*$/i);
                            if (prefixMatch) {
                                priceCurrency = prefixMatch[1];
                                price = prefixMatch[2].trim();
                                priceCurrencyPosition = 'prefix';
                            } else if (suffixMatch) {
                                price = suffixMatch[1].trim();
                                priceCurrency = suffixMatch[2];
                                priceCurrencyPosition = 'suffix';
                            }
                        }

                        return {
                            eyebrow: parsed.eyebrow || item.querySelector('.plan-eyebrow')?.innerHTML || '',
                            title: parsed.title || item.querySelector('.plan-title')?.innerHTML || '',
                            subtitle: parsed.subtitle || item.querySelector('.plan-subtitle')?.innerHTML || '',
                            price,
                            priceCurrency,
                            priceCurrencyPosition,
                            priceDetail: parsed.priceDetail || item.querySelector('.plan-price-detail')?.innerHTML || '',
                            features,
                            footerNote: parsed.footerNote || item.querySelector('.plan-footer-note')?.innerHTML || '',
                            ctaTitle: parsed.ctaTitle || item.querySelector('.plan-cta')?.textContent || '',
                            ctaLink: parsed.ctaLink || item.querySelector('.plan-cta')?.href || '',
                            buttonStyle: parsed.buttonStyle || item.querySelector('.plan-cta')?.dataset.buttonStyle || 'filled',
                            featured: parsed.featured ?? (item.dataset.featured === 'true'),
                            variant: parsed.variant || item.dataset.variant || ''
                        };
                    });
                    let currencies = [];
                    try {
                        currencies = block.dataset.ccCurrencies ? JSON.parse(block.dataset.ccCurrencies) : [];
                    } catch (e) { /* malformed attr — leave empty, switcher won't render */ }
                    return {
                        colorTheme: block.dataset.colorTheme || block.className.match(/theme-([a-z0-9-]+)/)?.[1] || 'white',
                        eyebrow: block.querySelector('.pricing-eyebrow')?.innerHTML || '',
                        title: block.querySelector('.pricing-title')?.innerHTML || '',
                        subtitle: block.querySelector('.pricing-subtitle')?.innerHTML || '',
                        helpTitle: block.querySelector('.pricing-help-title')?.innerHTML || '',
                        helpBody: block.querySelector('.pricing-help-body')?.innerHTML || '',
                        items: pricingPlanItems,
                        currentCurrency: block.dataset.ccCurrentCurrency || '',
                        currencies,
                        topBanner: block.dataset.topBannerVisible === 'true' ? {
                            text: block.dataset.topBannerText || '',
                            text2: block.dataset.topBannerText2 || '',
                            campaignText: block.dataset.topBannerCampaignText || '',
                            campaignText2: block.dataset.topBannerCampaignText2 || '',
                            bgColor: block.dataset.topBannerBg || '#5C33D7',
                            textColor: block.dataset.topBannerColor || '#ffffff',
                            link: block.dataset.topBannerLink || '',
                            size: block.dataset.topBannerSize || 'Small'
                        } : null
                    };
                }
                default:
                    console.warn('No extractBlockData handler for block type:', blockType);
                    return { primary: {} };
            }
        };

        let blockIndex = 0;

        const customBlocksContent = blocks.map(block => {
            const blockType = Object.keys(componentMap).find(type => {
                // Use regex word boundary to avoid partial matches
                // e.g. 'wp-block-cardcam-faq' should NOT match 'wp-block-cardcam-faq-section'
                const regex = new RegExp('(^|\\s)' + type.replace(/-/g, '\\-') + '(\\s|$)');
                return regex.test(block.className);
            });

            if (!blockType) return null;

            const SliceComponent = componentMap[blockType];
            const currentIndex = blockIndex++;
            const isVisible = block.dataset.sliceVisible !== 'false';


            if (!isVisible) return null;

            const data = extractBlockData(block, blockType);

            const blockTheme = block.className.match(/theme-([a-z0-9-]+)/)?.[1]
                || block.dataset?.colorTheme
                || data?.colorTheme
                || 'white';

            const sliceBorderMap = {
                'white': '#EBE8E0',
                'off-white': '#E8E2D9',
                'bright': '#EBE7E0',
                'semi-bright': '#DBD6CC',
                'semi-dark': '#9EA2A8',
                'dark': '#2A2336',
                'deep-purple': '#5C33D7',
                'colorful': '#E8E2D9',
                'colorful-yellow': '#D4AE00',
                'colorful-green': '#02563A',
                'blue-lighter': '#C8DBDB',
                'blue-light': '#A8C4C3',
                'blue-medium': '#6FADB3',
                'spacey-bright': '#B5D5D9',
                'spacey-dark': '#2A2336'
            };
            const sliceBorder = sliceBorderMap[blockTheme] || '#E8E2D9';
            const hideBottomBorder = block.dataset.hideBottomBorder === 'true';
            const sliceBorderSx = blockTheme === 'deep-purple'
                ? { borderTop: '3px solid rgb(92, 51, 215)', ...(hideBottomBorder ? {} : { borderBottom: '3px solid rgb(92, 51, 215)' }) }
                : (hideBottomBorder ? {} : { borderBottom: `1px solid ${sliceBorder}` });

            return (
                <Suspense key={currentIndex}>
                    <Box
                        id={block.id || null}
                        className="slice-container"
                        sx={{ width: '100%', ...sliceBorderSx }}
                    >
                        <SliceComponent
                            data={data}
                            sliceData={data}
                            slice={data}
                            index={currentIndex}
                            direction={currentIndex % 2 !== 1 ? "row" : "row-reverse"}
                            pageType={pageData.type || "page"}
                        />
                    </Box>
                </Suspense>
            );
        }).filter(Boolean);

        console.log('Custom blocks content length:', customBlocksContent.length);
        console.log('Should show fallback?', customBlocksContent.length === 0);

        // If no custom blocks found, render regular WordPress content
        if (customBlocksContent.length === 0) {
            console.log('🎨 RENDERING ENHANCED WORDPRESS CONTENT');
            return (
                <Box
                    className="slice"
                    px={{ xs: 2, md: 4 }}
                    pt={{ xs: 2, md: 4 }}
                    pb={{ xs: 2, md: 4 }}
                    sx={{
                        background: (theme) => theme.palette.background.default,
                        minHeight: '100vh'
                    }}
                >
                    <Box
                        sx={{
                            maxWidth: '1200px',
                            mx: 'auto',
                            mt: 8,
                            '& h1': {
                                fontSize: { xs: '2rem', md: '2.5rem' },
                                fontWeight: 600,
                                color: 'text.primary',
                                mb: 4,
                                textAlign: 'center'
                            },
                            '& h2': {
                                fontSize: { xs: '1.5rem', md: '2rem' },
                                fontWeight: 600,
                                color: 'text.primary',
                                mt: 4,
                                mb: 3
                            },
                            '& h3': {
                                fontSize: { xs: '1.25rem', md: '1.5rem' },
                                fontWeight: 500,
                                color: 'text.primary',
                                mt: 3,
                                mb: 2
                            },
                            '& h4, & h5, & h6': {
                                fontWeight: 500,
                                color: 'text.primary',
                                mt: 2,
                                mb: 2
                            },
                            '& p': {
                                color: 'text.secondary',
                                mb: 3,
                                lineHeight: 1.7,
                                fontSize: { xs: '1rem', md: '1.1rem' }
                            },
                            '& p strong': {
                                color: 'text.primary',
                                fontWeight: 600
                            },
                            '& a': {
                                color: 'primary.main',
                                textDecoration: 'underline',
                                '&:hover': {
                                    textDecoration: 'none'
                                }
                            },
                            '& ul, & ol': {
                                color: 'text.secondary',
                                mb: 3,
                                pl: 3,
                                lineHeight: 1.7,
                                '& li': {
                                    mb: 1
                                }
                            },
                            '& blockquote': {
                                borderLeft: '4px solid',
                                borderColor: 'primary.main',
                                pl: 3,
                                ml: 0,
                                fontStyle: 'italic',
                                color: 'text.secondary'
                            }
                        }}
                        dangerouslySetInnerHTML={{ __html: pageData.content.rendered }}
                    />
                </Box>
            );
        }

        return customBlocksContent;
    };

    if (isLoading) {
        return <div style={{ width: "100%", minHeight: "100vh", backgroundColor: "#fffefb" }} />;
    }

    // No page/post found for this slug → redirect to language-aware root
    if (!pageData) {
        const lang = detectLang(location.pathname);
        const rootPath = lang === DEFAULT_LANG ? '/' : `/${lang}/`;
        if (location.pathname !== rootPath) {
            return <Navigate to={rootPath} replace />;
        }
    }

    // Check if page should be displayed as subpage using the new display_mode field
    const isSubpage = pageData?.seo_meta?.display_mode === 'subpage';

    // Get the page's color theme, default to 'white'
    const pageColorTheme = pageData?.seo_meta?.color_theme || 'white';

    // Debug logging for data structure
    console.log('📊 Page data structure:', {
        hasPageData: !!pageData,
        hasSeoMeta: !!pageData?.seo_meta,
        colorTheme: pageColorTheme,
        displayMode: pageData?.seo_meta?.display_mode,
        pageDataKeys: pageData ? Object.keys(pageData) : [],
        fullPageData: pageData
    });

    // Map WordPress theme names to generateTheme names
    const themeMap = {
        'white': 'white',
        'off-white': 'off-white',
        'bright': 'bright',
        'semi-bright': 'semi-bright',
        'semi-dark': 'semi-dark',
        'dark': 'dark',
        'deep-purple': 'deep purple',
        'spacey-dark': 'dark',
        'spacey-bright': 'spacey bright',
        'colorful': 'colorful',
        'blue-lighter': 'blue lighter',
        'colorful-yellow': 'colorful yellow',
        'colorful-surprise': 'colorful yellow', // fallback
        'colorful-green': 'colorful green'
    };

    const themeName = themeMap[pageColorTheme] || 'white';
    console.log('Page color theme from WordPress:', pageColorTheme);
    console.log('Mapped theme name:', themeName);
    const theme = generateTheme(themeName);

    // Check if this is a regular WordPress content page (no custom blocks)
    const hasCustomBlocks = () => {
        if (!pageData?.content?.rendered) return false;
        const parser = new DOMParser();
        const doc = parser.parseFromString(pageData.content.rendered, 'text/html');
        const componentMap = {
            'wp-block-cardcam-hero': TopHeroSlice,
            'wp-block-cardcam-key-features': KeyFeaturesSlice,
            'wp-block-cardcam-text-image': TextImageSlice,
            'wp-block-cardcam-video-text': TextImageSlice,
            'wp-block-cardcam-text-two-cols': TextTwoCols,
            'wp-block-cardcam-selling-points': SellingPointsSlice,
            'wp-block-cardcam-pricing-plan': PricingPlanSlice,
            'wp-block-cardcam-pricing': PriceSlice,
            'wp-block-cardcam-contact': ContactSlice,
            'wp-block-cardcam-customer-logos': CustomerLogos,
            'wp-block-cardcam-customer-stories': CustomerStoriesSlice,
            'wp-block-cardcam-faq-short': FaqShortSlice,
            'wp-block-cardcam-faq-section': Faq,
            'wp-block-cardcam-faq': Faq,
            'wp-block-cardcam-image-full': ImageFullwidth,
            // wp-block-cardcam-footer intentionally excluded — footer is PHP-rendered (footer.php)
            'wp-block-cardcam-call-to-action': CallToActionSlice,
            'wp-block-cardcam-info-grid': InfoGridSlice,
            'wp-block-cardcam-global-reach': GlobalReachSlice,
            'wp-block-cardcam-kpi-grid': KpiGridSlice,
            'wp-block-cardcam-final-cta': FinalCtaSlice,
            'wp-block-cardcam-section-intro': SectionIntroSlice,
            'wp-block-cardcam-features-grid': FeaturesGridSlice,
            'wp-block-cardcam-comparison': ComparisonSlice,
            'wp-block-cardcam-thank-you': ThankYouSlice
        };
        const blocks = Array.from(doc.body.children).filter(node => {
            if (!node.className) return false;
            if (node.className.includes('wp-block-cardcam-menu')) return false;
            return Object.keys(componentMap).some(type => {
                const regex = new RegExp('(^|\\s)' + type.replace(/-/g, '\\-') + '(\\s|$)');
                return regex.test(node.className);
            });
        });
        return blocks.length > 0;
    };

    // If this is a regular WordPress page, render with enhanced styling
    if (!hasCustomBlocks() && pageData?.content?.rendered) {
        console.log('🎯 RENDERING COMPLETE ENHANCED PAGE');
        const isPost = pageData?.type === 'post';
        const RendererComponent = isPost ? DefaultPostRenderer : DefaultPageRenderer;

        return (
            <ThemeProvider theme={theme}>
                <div className="App">
                    {getPageMetadata() && <MetaTagsHelper prismicPage={getPageMetadata()} />}
                    <main>
                        <Container disableGutters maxWidth={false} sx={{ background: (theme) => theme.palette.background.default, minHeight: "100vh" }}>
                            <Suspense>
                                <RendererComponent
                                    pageData={pageData}
                                    postData={pageData}
                                    colorTheme={themeName}
                                />
                            </Suspense>
                        </Container>
                    </main>
                </div>
            </ThemeProvider>
        );
    }

    // Get subpage header data from WordPress post meta (via seo_meta API field)
    const getSubpageHeaderData = () => {
        const seo = pageData?.seo_meta;
        if (!seo?.subpage_title) return null;

        return {
            title1: seo.subpage_title,
            subtitle: seo.subpage_subtitle || '',
            link: seo.subpage_link_url ? { url: seo.subpage_link_url } : null,
            linktext: seo.subpage_link_text || '',
            color_theme: 'white'
        };
    };

    if (isSubpage) {
        const subpageHeader = getSubpageHeaderData();

        return (
            <ThemeProvider theme={theme}>
                <div className="App">
                    {getPageMetadata() && <MetaTagsHelper prismicPage={getPageMetadata()} />}
                    <main>
                        <Container disableGutters maxWidth={false} sx={{ backgroundColor: "background.default", minHeight: "100dvh" }}>
                            <Stack
                                direction="column"
                                spacing={0}
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "stretch",
                                    width: "100%",
                                    maxWidth: { sm: "100%", lg: "1000px" },
                                    margin: "auto",
                                    padding: "0px 24px 0px 24px",
                                    minHeight: "100dvh"
                                }}
                            >
                                {subpageHeader && (
                                    <Suspense>
                                        <TopSubpageSlice sliceData={subpageHeader} />
                                    </Suspense>
                                )}
                                {generateContent()}
                            </Stack>
                        </Container>
                    </main>
                </div>
            </ThemeProvider>
        );
    }

    return (
        <ThemeProvider theme={theme}>
            <div className="App">
                {getPageMetadata() && <MetaTagsHelper prismicPage={getPageMetadata()} />}
                <main>
                    <Container disableGutters maxWidth={false} sx={{ background: (theme) => theme.palette.background.default }}>
                        {generateContent()}
                    </Container>
                </main>
            </div>
        </ThemeProvider>
    );
}

export default App;